<template>
  <div class="stage-leaderboard">
    <!-- 阶段选择 -->
    <div class="stage-selector">
      <el-select 
        v-model="selectedStage" 
        @change="loadLeaderboard"
        placeholder="选择学习阶段"
        size="large"
      >
        <el-option
          v-for="stage in stages"
          :key="stage.id"
          :label="stage.name"
          :value="stage.id"
        ></el-option>
      </el-select>
    </div>
    
    <!-- 排行榜列表 -->
    <div class="leaderboard-list" v-if="selectedStage">
      <div class="leaderboard-header">
        <h3>{{ getStageName(selectedStage) }} - 完成排行榜</h3>
        <!-- <p>按完成时长排名，用时越短排名越高</p> -->
      </div>
      
      <!-- 用户排名卡片 -->
      <div v-if="userRank" class="user-rank-card">
        <div class="rank-info">
          <span class="rank-number">#{{ userRank.rank }}</span>
          <span class="rank-text">您的排名</span>
        </div>
        <div class="rank-details">
          <div class="detail-item">
            <span class="label">完成时长</span>
            <span class="value">{{ userRank.completionDuration }}</span>
          </div>
          <div class="detail-item">
            <span class="label">学习天数</span>
            <span class="value">{{ userRank.studyDays }}天</span>
          </div>
          <div class="detail-item">
            <span class="label">平均正确率</span>
            <span class="value">{{ userRank.averageAccuracy }}%</span>
          </div>
        </div>
      </div>
      
      <!-- 排行榜列表 -->
      <div class="rank-list">
        <div
          v-for="item in leaderboard"
          :key="item.userId"
          class="rank-item"
          :class="{ 'current-user': item.isCurrentUser }"
        >
          <div class="rank-position">
            <span class="position-number">{{ item.rank }}</span>
            <div class="medal" v-if="item.rank <= 3">
              <i :class="getMedalClass(item.rank)"></i>
            </div>
          </div>
          
          <div class="user-info">
            <img 
              :src="item.avatar || defaultAvatar" 
              :alt="item.nickname || item.username" 
              class="avatar" 
            />
            <div class="user-details">
              <span class="username">{{ item.nickname || item.username }} <span class="user-tag" v-if="item.user_tag">{{ item.user_tag }}</span></span>
              <span class="completion-time">{{ item.completionDuration }}</span>
            </div>
          </div>
          
          <div class="stats">
            <div class="stat-item">
              <span class="stat-label">学习天数</span>
              <span class="stat-value">{{ item.studyDays }}天</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">正确率</span>
              <span class="stat-value">{{ item.averageAccuracy }}%</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 分页 -->
      <div class="pagination" v-if="pagination.totalPages > 1">
        <el-pagination
          @current-change="handlePageChange"
          :current-page="pagination.currentPage"
          :page-size="pagination.pageSize"
          :total="pagination.totalCount"
          layout="prev, pager, next"
        ></el-pagination>
      </div>
    </div>
    
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container" v-loading="loading">
      <p>加载中...</p>
    </div>
    
    <!-- 空状态 -->
    <div v-if="!loading && !selectedStage" class="empty-state">
      <i class="el-icon-trophy"></i>
      <p>请选择一个学习阶段查看排行榜</p>
    </div>
  </div>
</template>

<script>
import { LeaderboardAPI } from "@/api/leaderboard";

export default {
  name: "StageLeaderboard",
  props: {
    stages: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedStage: null,
      leaderboard: [],
      userRank: null,
      pagination: {
        currentPage: 1,
        pageSize: 20,
        totalCount: 0,
        totalPages: 0
      },
      loading: false,
      defaultAvatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNGNUY1RjUiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0iIzk5OTk5OSIvPgo8cGF0aCBkPSJNMzIgMzJDMzIgMjYuNDc3MiAyNy41MjI4IDIyIDIyIDIySDE4QzEyLjQ3NzIgMjIgOCAyNi40NzcyIDggMzJWMzRIMzJWMzJaIiBmaWxsPSIjOTk5OTk5Ii8+Cjwvc3ZnPgo='
    };
  },
  methods: {
    // 加载排行榜数据
    async loadLeaderboard() {
      if (!this.selectedStage) return;
      
      this.loading = true;
      try {
        const response = await LeaderboardAPI.getStageLeaderboard(
          this.selectedStage,
          this.pagination.currentPage,
          this.pagination.pageSize
        );
        
        if (response.success) {
          const data = response.data;
          this.leaderboard = data.leaderboard || [];
          this.userRank = data.userRank;
          if (data.pagination) {
            this.pagination = {
              currentPage: Number(data.pagination.currentPage) || 1,
              pageSize: Number(data.pagination.pageSize) || 20,
              total: Number(data.pagination.total) || 0
            };
          }
        } else {
          this.$message.error(response.message || "加载排行榜失败");
        }
      } catch (error) {
        console.error("加载排行榜失败:", error);
        this.$message.error("加载排行榜失败");
      } finally {
        this.loading = false;
      }
    },
    
    // 获取阶段名称
    getStageName(stageId) {
      const stage = this.stages.find(s => s.id === stageId);
      return stage ? stage.name : `阶段${stageId}`;
    },
    
    // 获取奖牌图标类名
    getMedalClass(rank) {
      const medalClasses = {
        1: "el-icon-medal gold",
        2: "el-icon-medal silver", 
        3: "el-icon-medal bronze"
      };
      return medalClasses[rank] || "";
    },
    
    // 处理分页变化
    handlePageChange(page) {
      this.pagination.currentPage = page;
      this.loadLeaderboard();
    },
    
    // 刷新排行榜
    refresh() {
      this.loadLeaderboard();
    }
  },
  watch: {
    stages: {
      handler(newStages) {
        if (newStages.length > 0 && !this.selectedStage) {
          this.selectedStage = newStages[0].id;
          this.loadLeaderboard();
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
.stage-leaderboard {
  padding: 0;
  background: transparent;
  min-height: 500px;
}

.stage-selector {
  margin-bottom: 32px;
  text-align: center;
}

.stage-selector .el-select {
  width: 320px;
}

.stage-selector .el-select .el-input__inner {
  border-radius: 25px;
  border: 2px solid #e1e8ed;
  font-size: 16px;
  padding: 12px 20px;
  transition: all 0.3s ease;
}

.stage-selector .el-select .el-input__inner:focus {
  border-color: #4a90e2;
  box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

.leaderboard-header {
  text-align: center;
  margin-bottom: 32px;
  position: relative;
}

.leaderboard-header::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, #e1e8ed, transparent);
  z-index: 1;
}

.leaderboard-header h3 {
  font-size: 28px;
  color: #2c3e50;
  margin-bottom: 8px;
  font-weight: 700;
  position: relative;
  z-index: 2;
  background: white;
  padding: 0 20px;
  display: inline-block;
}

.leaderboard-header p {
  color: #7f8c8d;
  font-size: 15px;
  position: relative;
  z-index: 2;
  background: white;
  padding: 0 20px;
  display: inline-block;
}

.user-rank-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
}

.user-rank-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(180deg); }
}

.rank-info {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 2;
}

.rank-number {
  font-size: 36px;
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.rank-text {
  font-size: 18px;
  opacity: 0.95;
  font-weight: 500;
}

.rank-details {
  display: flex;
  gap: 32px;
  position: relative;
  z-index: 2;
}

.detail-item {
  text-align: center;
  background: rgba(255,255,255,0.15);
  padding: 12px 16px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.detail-item .label {
  display: block;
  font-size: 12px;
  opacity: 0.9;
  margin-bottom: 6px;
  font-weight: 500;
}

.detail-item .value {
  display: block;
  font-size: 18px;
  font-weight: 700;
}

.rank-list {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f2f5;
}

.rank-item {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f8f9fa;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.rank-item:last-child {
  border-bottom: none;
}

.rank-item:hover {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.rank-item.current-user {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-left: 5px solid #2196f3;
  box-shadow: 0 4px 16px rgba(33, 150, 243, 0.2);
}

.rank-position {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 80px;
}

.position-number {
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f8f9fa;
  transition: all 0.3s ease;
}

.rank-item:hover .position-number {
  background: #4a90e2;
  color: white;
  transform: scale(1.1);
}

.medal {
  position: relative;
}

.medal i {
  font-size: 24px;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
}

.medal i.gold {
  color: #ffd700;
  animation: glow 2s ease-in-out infinite alternate;
}

.medal i.silver {
  color: #c0c0c0;
}

.medal i.bronze {
  color: #cd7f32;
}

@keyframes glow {
  from { filter: drop-shadow(0 2px 4px rgba(255,215,0,0.3)); }
  to { filter: drop-shadow(0 2px 8px rgba(255,215,0,0.6)); }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  margin-left: 20px;
}

.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #f0f2f5;
  transition: all 0.3s ease;
}

.rank-item:hover .avatar {
  border-color: #4a90e2;
  transform: scale(1.05);
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.username {
  font-size: 17px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 2px;
}

.user-tag {
  display: inline-block;
  padding: 2px 10px;
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.user-tag:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.5);
}

.rank-item.current-user .user-tag {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.4);
}

.completion-time {
  font-size: 14px;
  color: #7f8c8d;
  font-weight: 500;
}

.stats {
  display: flex;
  gap: 32px;
}

.stat-item {
  text-align: center;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f8f9fa;
  transition: all 0.3s ease;
}

.rank-item:hover .stat-item {
  background: #e9ecef;
  transform: translateY(-1px);
}

.stat-label {
  display: block;
  font-size: 11px;
  color: #6c757d;
  margin-bottom: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #2c3e50;
}

.pagination {
  margin-top: 32px;
  text-align: center;
}

.pagination .el-pagination {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.pagination .el-pagination .el-pager li {
  border-radius: 8px;
  margin: 0 2px;
  transition: all 0.3s ease;
}

.pagination .el-pagination .el-pager li:hover {
  background: #4a90e2;
  color: white;
}

.loading-container {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #7f8c8d;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 20px;
  display: block;
  opacity: 0.6;
}

.empty-state p {
  font-size: 18px;
  font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .stage-leaderboard {
    padding: 0;
  }
  
  .stage-selector .el-select {
    width: 100%;
    max-width: 300px;
  }
  
  .leaderboard-header h3 {
    font-size: 24px;
  }
  
  .user-rank-card {
    flex-direction: column;
    gap: 20px;
    text-align: center;
    padding: 20px;
  }
  
  .rank-details {
    justify-content: center;
    gap: 16px;
  }
  
  .rank-item {
    flex-direction: column;
    gap: 16px;
    text-align: center;
    padding: 16px;
  }
  
  .user-info {
    margin-left: 0;
    flex-direction: column;
    gap: 12px;
  }
  
  .stats {
    justify-content: center;
    gap: 16px;
  }
  
  .rank-position {
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .rank-details {
    flex-direction: column;
    gap: 12px;
  }
  
  .stats {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
